import { useEffect, useRef } from "react";
import { useActivityStore } from "../store/useActivityStore";
import { useAttendanceStore } from "../store/useAttendanceStore";
import { useAuthStore } from "../store/useAuthStore";

const SYNC_INTERVAL_MS = 30 * 1000;
const IDLE_THRESHOLD_MS = 60 * 1000;
const BUFFER_STORAGE_KEY = "activity_event_buffer_v1";

type BufferedItem = { type: "summary" | "event"; payload: any; ts: string };

export const useActivityTracker = () => {
  const { authUser } = useAuthStore();
  const { clockedIn } = useAttendanceStore();
  const { startTracking } = useActivityStore();

  const activeMsRef = useRef<number>(0);
  const idleMsRef = useRef<number>(0);
  const lastEventAtRef = useRef<number>(Date.now());
  const isIdleRef = useRef<boolean>(false);
  const syncTimerRef = useRef<number | null>(null);
  const lastTickRef = useRef<number>(Date.now());

  const readBuffer = (): BufferedItem[] => {
    try {
      const raw = localStorage.getItem(BUFFER_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as BufferedItem[]) : [];
    } catch {
      return [];
    }
  };
  const writeBuffer = (buf: BufferedItem[]) =>
    localStorage.setItem(BUFFER_STORAGE_KEY, JSON.stringify(buf));
  const pushToBuffer = (item: BufferedItem) => {
    const b = readBuffer();
    b.push(item);
    writeBuffer(b);
  };
  const flushBuffer = async () => {
    const buf = readBuffer();
    if (!buf.length) return;
    try {
      for (const item of buf) {
        if (item.type === "summary") {
          await fetch("/api/activity/record", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(item.payload),
          });
        } else {
          await fetch("/api/activity/event", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(item.payload),
          });
        }
      }
      localStorage.removeItem(BUFFER_STORAGE_KEY);
    } catch (err) {
      console.warn("flushBuffer failed:", err);
    }
  };

  const onUserActivity = (evtName = "interaction") => {
    const now = Date.now();
    const delta = Math.max(0, now - lastEventAtRef.current);
    if (isIdleRef.current) {

      pushToBuffer({
        type: "event",
        payload: { kind: "idle->active", at: new Date().toISOString(), reason: evtName },
        ts: new Date().toISOString(),
      });
      isIdleRef.current = false;
    } else {
      activeMsRef.current += delta;
    }
    lastEventAtRef.current = now;
  };

  const tick = async () => {
    const now = Date.now();
    const sinceLast = Math.max(0, now - lastTickRef.current);
    lastTickRef.current = now;

    const sinceUser = now - lastEventAtRef.current;
    if (sinceUser >= IDLE_THRESHOLD_MS) {
      isIdleRef.current = true;
      idleMsRef.current += sinceLast;
    } else {
      activeMsRef.current += sinceLast;
    }

    const activeMs = activeMsRef.current;
    const idleMs = idleMsRef.current;
    if (activeMs > 0 || idleMs > 0) {
      const payload = { activeMs, idleMs };
      try {
        const r = await fetch("/api/activity/record", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error("server error");
        activeMsRef.current = 0;
        idleMsRef.current = 0;
        await flushBuffer();
      } catch (err) {
        pushToBuffer({ type: "summary", payload, ts: new Date().toISOString() });
      }
    }
  };

  useEffect(() => {
    if (!authUser || !clockedIn) {
      if (syncTimerRef.current) {
        window.clearInterval(syncTimerRef.current);
        syncTimerRef.current = null;
      }
      activeMsRef.current = 0;
      idleMsRef.current = 0;
      isIdleRef.current = false;
      lastEventAtRef.current = Date.now();
      lastTickRef.current = Date.now();
      return;
    }

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    const handler = () => onUserActivity("interaction");
    events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));
    const visChange = () => {
      if (document.visibilityState === "visible") onUserActivity("visibilityVisible");
      else lastEventAtRef.current = Date.now() - IDLE_THRESHOLD_MS - 1;
    };
    window.addEventListener("visibilitychange", visChange);
    window.addEventListener("focus", () => onUserActivity("focus"));
    window.addEventListener("blur", () => (lastEventAtRef.current = Date.now() - IDLE_THRESHOLD_MS - 1));

    lastEventAtRef.current = Date.now();
    lastTickRef.current = Date.now();

    startTracking().catch((e) => console.warn("startTracking failed", e));

    if (!syncTimerRef.current) {
      syncTimerRef.current = window.setInterval(() => {
        tick().catch((e) => console.warn("tick error", e));
      }, SYNC_INTERVAL_MS) as unknown as number;
    }

    flushBuffer().catch(() => {});

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handler));
      window.removeEventListener("visibilitychange", visChange);
      if (syncTimerRef.current) {
        window.clearInterval(syncTimerRef.current);
        syncTimerRef.current = null;
      }
      tick().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, clockedIn]);

  useEffect(() => {
    const onOnline = () => flushBuffer();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);
};
